import { API } from "../API";
import { Mixpanel } from "../context/AnalyticsService";
import {
  Campaign,
  DefaultLinkTrackerAttachmentId,
  DefaultLinkTrackerName,
  Event,
  EventType,
  MassText,
  MassTextAttachment,
  MassTextAttachmentType,
} from "@markit/common.types";
import { getCountFromServer, getDocs, limit, query, where } from "../firebase";
import {
  getTextRecipientsRef,
  getTicketsRef,
  getUserEventData,
  getUserEventLinkTrackersRef,
  getUserEventsRef,
} from "./FirebaseUtils";
import {
  LessThanDate,
  isExternalEventbrite,
  productsWithConversions,
} from "@markit/common.utils";
import { MassTextDetails } from "../redux/slices/campaignSlice";
import { TopCreators } from "./ExploreUtils";

/**
 * Handles the link open for all types of link tracking provided the linkId.
 * Called when the user views the event link with the link id attached.
 * 1. Mass Text Link Tracking:
 *    Updates the specified user in the link data their TextRecipient open tracking.
 * 2. Event Link Tracking:
 *    Updates the EventLinkTracker related to the link and adds an open action.
 */
export const updateLinkTrackerOpens = async (
  eventId: string,
  eventUserId: string,
  linkId: string
) => {
  try {
    if (linkId !== "") {
      await API.tracking.updateLinkTrackerOpens({
        eventId: eventId,
        eventUserId: eventUserId,
        linkId: linkId,
      });
    }
  } catch (err: any) {
    console.error(err.message);
  }
};

/**
 * Increments the num opens field on the default markit event tracker used for
 * tracking an event's total link views.
 */
export const updateTotalEventLinkViews = async (
  eventId: string,
  eventUserId: string
) => {
  try {
    await API.tracking.updateTotalEventLinkViews({
      eventId: eventId,
      eventUserId: eventUserId,
    });
  } catch (err: any) {
    console.error(err.message);
  }
};

// callback function for generating / saving changes to tracking links
export const generateTrackingLinksOnPress = async (
  event: Event,
  linkTrackerId: string | undefined,
  trackingLinkModalText: string,
  mixpanel: Mixpanel,
  userId: string
) => {
  if (linkTrackerId) {
    await API.tracking
      .updateEventLinkTracker({
        userId: event.createdBy,
        eventId: event.id,
        linkTrackerId: linkTrackerId,
        newTitle: trackingLinkModalText,
      })
      .then(() => {
        mixpanel.track("Edit Event Link Tracker", {
          new_link_name: trackingLinkModalText,
          event_id: event.id,
        });
      });
  } else {
    await API.tracking
      .generateNewEventLinkTracker({
        title: trackingLinkModalText,
        eventUserId: event.createdBy,
        eventId: event.id,
        sentBy: userId,
      })
      .then(() => {
        mixpanel.track("Generate New Event Link Tracker", {
          link_name: trackingLinkModalText,
          event_id: event.id,
        });
      });
  }
};

// gets the number of event link trackers a user has for an event
export const getTotalNumUserEventLinkTrackers = async (
  eventUserId: string,
  eventId: string
): Promise<number> => {
  const userEventLinkTrackersRef = await getUserEventLinkTrackersRef(
    eventUserId,
    eventId
  );
  // filter out the default event tracker
  const query_ = query(
    userEventLinkTrackersRef,
    where("alias", "!=", DefaultLinkTrackerName.DEFAULT_EVENT_LINK_TRACKER)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count;
};

// Gets the total numOpens and numConversions for the campaign accumulated of all the mass texts
export const getCampaignLinkAnalytics = async (
  massTextDetails: MassTextDetails[],
  campaign: Campaign
): Promise<{
  totalOpens: number;
  totalConversions: number;
}> => {
  if (LessThanDate("2024-11-26T17:00:00.000Z", campaign.createdAt)) {
    let numTotalOpens = 0;
    let numTotalConversions = 0;
    for (let i = 0; i < massTextDetails.length; i++) {
      const { attachments } = massTextDetails[i];
      numTotalOpens += attachments.reduce(
        (prev, curr) => prev + curr.numOpens,
        0
      );
      numTotalConversions += attachments.reduce(
        (prev, curr) => prev + curr.numConversions,
        0
      );
    }
    return { totalOpens: numTotalOpens, totalConversions: numTotalConversions };
  } else {
    const results = await Promise.all(
      massTextDetails.map(async (massTextDetail) => {
        let numOpens = 0;
        let numConversions = 0;
        const { massText, attachments } = massTextDetail;
        const promises = attachments.map(async (attachment) => {
          const [opensResponse, conversionsResponse] = await Promise.all([
            API.tracking.getAttachmentNumOpens({
              userId: massText.sentBy,
              massTextId: massText.id,
              campaignId: massText.campaignId,
              attachmentId: attachment.id,
            }),
            API.tracking.getAttachmentNumConversions({
              userId: massText.sentBy,
              massTextId: massText.id,
              campaignId: massText.campaignId,
              attachmentId: attachment.id,
            }),
          ]);
          return {
            opens: opensResponse.totalNum,
            conversions: conversionsResponse.totalNum,
          };
        });
        const results = await Promise.all(promises);
        results.forEach(({ opens, conversions }) => {
          numOpens += opens;
          numConversions += conversions;
        });
        return { massTextOpens: numOpens, massTextConversions: numConversions };
      })
    );
    const totalOpens = results.reduce((sum, r) => sum + r.massTextOpens, 0);
    const totalConversions = results.reduce(
      (sum, r) => sum + r.massTextConversions,
      0
    );
    return { totalOpens, totalConversions };
  }
};

// Gets the numOpens and numConversions of the attachment
export const getMassTextAttachmentsLinkAnalytics = async (
  userId: string,
  massText: MassText,
  attachment: MassTextAttachment
): Promise<{ numOpens: number; numConversions: number }> => {
  let numOpens = 0;
  let numConversions = 0;
  // If the mass text was sent after the specified timestamp, get the opens and conversions from the massTextAttachment itself.
  // The else condition gets the link analytics for mass texts sent prior to Campaigns V1
  if (LessThanDate("2024-11-26T17:00:00.000Z", massText.sentAt)) {
    numOpens = attachment.numOpens;
    numConversions = attachment.numConversions;
  } else {
    const responses = await Promise.all([
      API.tracking.getAttachmentNumOpens({
        userId: userId,
        massTextId: massText.id,
        campaignId: massText.campaignId,
        attachmentId: attachment.id,
      }),
      API.tracking.getAttachmentNumConversions({
        userId: userId,
        massTextId: massText.id,
        campaignId: massText.campaignId,
        attachmentId: attachment.id,
      }),
    ]);
    numOpens = responses[0].totalNum;
    numConversions = responses[1].totalNum;
  }
  return {
    numOpens: numOpens,
    numConversions: numConversions,
  };
};

// Gets the total link views for the default event link tracker (useful for generic tracking links)
export const getTotalLinkViews = async (userId: string, eventId: string) => {
  const eventLinkTrackersRef = getUserEventLinkTrackersRef(userId, eventId);
  const totalLinkViewsQuery = query(
    eventLinkTrackersRef,
    where("alias", "==", DefaultLinkTrackerName.DEFAULT_EVENT_LINK_TRACKER),
    limit(1)
  );
  const snapshot = await getDocs(totalLinkViewsQuery);
  if (!snapshot.empty) {
    const numOpens =
      snapshot.docs[0].data().trackingOverviews[DefaultLinkTrackerAttachmentId]
        .numOpens;
    return numOpens;
  }
  return 0;
};

// Show the conversions for a text if the link is a product/event with checkout or if the text attachment has conversions.
// Eventbrite events never record text conversions, so they're excluded.
export const showTextConversions = (
  event: Event | undefined,
  massTextAttachment: MassTextAttachment
) => {
  return (
    (!!event &&
      !isExternalEventbrite(event.eventType) &&
      productsWithConversions(event.eventType, event.accessLevel)) ||
    massTextAttachment.numConversions > 0
  );
};

// Calculate conversion revenue for multiple text attachments with a single text recipient fetch.
export const calculateRevenueFromTextConversionsByAttachment = async (
  massText: MassText,
  attachments: MassTextAttachment[]
): Promise<Map<string, number>> => {
  const revenueByAttachment = new Map<string, number>();
  if (attachments.length === 0) {
    return revenueByAttachment;
  }

  // attachment id -> set of converted recipient uids
  const convertedRecipientIdsByAttachment = new Map<string, Set<string>>();
  // event id -> attachment ids (only attachments that have an eventId)
  const attachmentIdsByEvent = new Map<string, string[]>();

  // Initialize maps for all attachments up front
  attachments.forEach(({ id }) => {
    revenueByAttachment.set(id, 0);
    convertedRecipientIdsByAttachment.set(id, new Set<string>());
  });

  // Special-case: for texts sent by a comedy store, try to resolve each attachment
  // to its specific event. Falls back to a time window for attachments that can't be resolved.
  if (
    massText.sentAt &&
    (massText.sentBy === TopCreators.THECOMEDYSTORE ||
      massText.sentBy === TopCreators.COMEDY_STORE_LJ)
  ) {
    const unresolvedAttachments: MassTextAttachment[] = [];

    await Promise.all(
      attachments.map(async (attachment) => {
        let resolvedEventId: string | null = null;

        if (
          attachment.massTextType === MassTextAttachmentType.LINK &&
          attachment.eventId
        ) {
          // Direct Markit event link — eventId is already the show's Markit event ID
          resolvedEventId = attachment.eventId;
        } else if (
          attachment.massTextType === MassTextAttachmentType.TRACKING_LINK &&
          attachment.eventId
        ) {
          // Tracking link: attachment.eventId points to a GENERIC_LINK_NO_DATA event
          // whose externalLink holds the Leap/Showclix URL. Match on that full URL.
          const userEventData = await getUserEventData(
            massText.sentBy,
            attachment.eventId
          );
          if (userEventData?.externalLink) {
            const matchingSnap = await getDocs(
              query(
                getUserEventsRef(massText.sentBy),
                where("eventType", "==", EventType.EXTERNAL_SHOWCLIX),
                where("externalLink", "==", userEventData.externalLink),
                limit(1)
              )
            );
            if (!matchingSnap.empty) {
              resolvedEventId = matchingSnap.docs[0].data().id;
            }
          }
        }

        if (resolvedEventId) {
          const ids = attachmentIdsByEvent.get(resolvedEventId) ?? [];
          ids.push(attachment.id);
          attachmentIdsByEvent.set(resolvedEventId, ids);
        } else {
          unresolvedAttachments.push(attachment);
        }
      })
    );

    // For attachments that couldn't be resolved to a specific event, fall back to the time window
    if (unresolvedAttachments.length > 0) {
      const sentAtDate = new Date(massText.sentAt);
      const eightDaysAfter = new Date(
        sentAtDate.getTime() + 8 * 24 * 60 * 60 * 1000
      );

      const eventsSnapshot = await getDocs(
        query(
          getUserEventsRef(massText.sentBy),
          where("start", ">", massText.sentAt),
          where("start", "<", eightDaysAfter.toISOString())
        )
      );

      const unresolvedAttachmentIds = unresolvedAttachments.map(({ id }) => id);
      eventsSnapshot.docs.forEach((eventDoc) => {
        const existing = attachmentIdsByEvent.get(eventDoc.id) ?? [];
        unresolvedAttachmentIds.forEach((id) => {
          if (!existing.includes(id)) {
            existing.push(id);
          }
        });
        attachmentIdsByEvent.set(eventDoc.id, existing);
      });
    }
  } else {
    // Normal case: for all other accounts, we can directly map the attachment to the event
    attachments.forEach(({ id, eventId }) => {
      if (!eventId) {
        return;
      }
      const ids = attachmentIdsByEvent.get(eventId) ?? [];
      ids.push(id);
      attachmentIdsByEvent.set(eventId, ids);
    });
  }

  // Build a set of attachment ids that actually map to events so we only query recipients
  // for attachments that can contribute to revenue.
  const attachmentIdsNeedingRecipients = new Set<string>();
  attachmentIdsByEvent.forEach((ids) => {
    ids.forEach((id) => attachmentIdsNeedingRecipients.add(id));
  });

  // For each relevant attachment, query only recipients where that attachment has conversions.
  await Promise.all(
    Array.from(attachmentIdsNeedingRecipients).map(async (attachmentId) => {
      const recipientsSnapshot = await getDocs(
        query(
          getTextRecipientsRef(
            massText.sentBy,
            massText.campaignId,
            massText.id
          ),
          where(`trackingOverviews.${attachmentId}.numConversions`, ">", 0)
        )
      );

      const convertedSet =
        convertedRecipientIdsByAttachment.get(attachmentId) ??
        new Set<string>();
      recipientsSnapshot.docs.forEach((recipientDoc) => {
        const recipient = recipientDoc.data();
        convertedSet.add(recipient.uid);
      });
      convertedRecipientIdsByAttachment.set(attachmentId, convertedSet);
    })
  );

  await Promise.all(
    Array.from(attachmentIdsByEvent.entries()).map(
      async ([eventId, eventAttachmentIds]) => {
        // Derive the event-level set of converters inline to skip the ticket query if empty
        const convertedUidsForEvent = new Set<string>();
        eventAttachmentIds.forEach((id) =>
          convertedRecipientIdsByAttachment
            .get(id)
            ?.forEach((uid) => convertedUidsForEvent.add(uid))
        );
        if (convertedUidsForEvent.size === 0) {
          return;
        }

        const convertedUidsArray = Array.from(convertedUidsForEvent);
        // Split into chunks of 30 to avoid hitting the limit of 30 where clauses
        const chunks: string[][] = [];
        for (let i = 0; i < convertedUidsArray.length; i += 30) {
          chunks.push(convertedUidsArray.slice(i, i + 30));
        }

        const ticketSnapshots = await Promise.all(
          chunks.map((chunk) =>
            getDocs(query(getTicketsRef(eventId), where("uid", "in", chunk)))
          )
        );

        const revenueByUid = new Map<string, number>();
        ticketSnapshots
          .flatMap((snap) => snap.docs)
          .forEach((ticketDoc) => {
            const ticket = ticketDoc.data();
            if (ticket.amountPaid > 0) {
              revenueByUid.set(
                ticket.uid,
                (revenueByUid.get(ticket.uid) ?? 0) + ticket.amountPaid / 100
              );
            }
          });

        eventAttachmentIds.forEach((attachmentId) => {
          const convertedUids =
            convertedRecipientIdsByAttachment.get(attachmentId);
          if (!convertedUids || convertedUids.size === 0) {
            return;
          }
          let totalRevenue = 0;
          convertedUids.forEach((uid) => {
            totalRevenue += revenueByUid.get(uid) ?? 0;
          });
          const previous = revenueByAttachment.get(attachmentId) ?? 0;
          revenueByAttachment.set(attachmentId, previous + totalRevenue);
        });
      }
    )
  );

  return revenueByAttachment;
};

// Calculate the total revenue from text conversions for a given mass text with event link
// Looks at the text recipients on the text that have conversions and then looks at the event attendees tickets that are part of the textRecipient ids
export const calculateRevenueFromTextConversions = async (
  massText: MassText,
  massTextAttachment: MassTextAttachment
): Promise<number> => {
  const revenueByAttachment =
    await calculateRevenueFromTextConversionsByAttachment(massText, [
      massTextAttachment,
    ]);
  return revenueByAttachment.get(massTextAttachment.id) ?? 0;
};
